import type { LiveStorage, StoredState } from "./types";

const STORE = "state";

/**
 * Raw IndexedDB, one record per profile keyed by its pseudonymous id. A wrapper library
 * would save a few lines but adds a dependency to the child's device for no behaviour.
 */
export function indexedDbStorage(dbName = "tutor-live"): LiveStorage {
  let opening: Promise<IDBDatabase> | null = null;

  function db(): Promise<IDBDatabase> {
    opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE);
      };
      request.onsuccess = () => {
        const opened = request.result;
        // Another tab upgrading the schema must not be blocked by this one.
        opened.onversionchange = () => {
          opened.close();
          opening = null;
        };
        resolve(opened);
      };
      request.onerror = () => {
        opening = null;
        reject(request.error ?? new Error("indexedDB open failed"));
      };
    });
    return opening;
  }

  async function run<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await db();
    return new Promise<T>((resolve, reject) => {
      const tx = database.transaction(STORE, mode);
      const request = action(tx.objectStore(STORE));
      // Resolve on commit, not on request success, so a save is durable when it resolves.
      tx.oncomplete = () => {
        resolve(request.result);
      };
      tx.onerror = () => {
        reject(tx.error ?? new Error("indexedDB transaction failed"));
      };
      tx.onabort = () => {
        reject(tx.error ?? new Error("indexedDB transaction aborted"));
      };
    });
  }

  return {
    async load(profileId) {
      const value: unknown = await run("readonly", (store) => store.get(profileId));
      return isStoredState(value) ? value : null;
    },
    async save(profileId, state) {
      await run("readwrite", (store) => store.put(state, profileId));
    },
    async clear(profileId) {
      await run("readwrite", (store) => store.delete(profileId));
    },
  };
}

/** In-memory storage for tests; copies on save and load like a structured clone would. */
export function memoryStorage(): LiveStorage {
  const states = new Map<string, StoredState>();
  return {
    load(profileId) {
      const state = states.get(profileId);
      return Promise.resolve(state === undefined ? null : structuredClone(state));
    },
    save(profileId, state) {
      states.set(profileId, structuredClone(state));
      return Promise.resolve();
    },
    clear(profileId) {
      states.delete(profileId);
      return Promise.resolve();
    },
  };
}

// A record written by an older app version, or a damaged one, is dropped rather than
// rendered: the actor's log is the source of truth and a replay from 0 rebuilds it.
function isStoredState(value: unknown): value is StoredState {
  if (typeof value !== "object" || value === null) return false;
  if (!("view" in value) || !("outbox" in value)) return false;
  const { view, outbox } = value;
  return (
    typeof view === "object" &&
    view !== null &&
    "headSeq" in view &&
    typeof view.headSeq === "number" &&
    "messages" in view &&
    Array.isArray(view.messages) &&
    Array.isArray(outbox) &&
    outbox.every((frame) => typeof frame === "string")
  );
}
